/**
 * The General tab's collapsed one-liners and the day strip's span label,
 * computed from the live draft (Phase 21: one list of drops; Phase 33: the
 * days it runs and the weekend share).
 * For Media Devoted | Created By Ciro Autiero
 */
import { describeRunDays, describeWeekendShare } from '@trafficpulse/shared';

import type { ContainerDraft } from '../../../../drop-generator.types';
import { minutesLabel } from '../../../../utils/format';

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function scheduleSummary(draft: ContainerDraft): string {
  const startLabel =
    draft.startMode === 'AFTER_PREVIOUS'
      ? `after prev${draft.gapMin > 0 ? ` + ${draft.gapMin}m` : ''}`
      : draft.startMode === 'FIXED_TIME'
        ? draft.startAtMin !== null
          ? `🇩🇰 ${minutesLabel(draft.startAtMin)}`
          : 'fixed (unset)'
        : 'start of day';
  // Start and end are Danish times (15/10/2026) — said so in the one-liner.
  const end = draft.endAtMin !== null ? ` → ${draft.startMode === 'FIXED_TIME' ? '' : '🇩🇰 '}${minutesLabel(draft.endAtMin)}` : '';
  const days = describeRunDays(draft.runDays);
  return [
    `${startLabel}${end}`,
    days === 'every day' ? '' : days,
    describeWeekendShare(draft.runDays, draft.weekendAmountPct) ?? '',
    `every ${draft.intervalMin} min`,
    `${draft.defaultAmount.toLocaleString('en-US')}/drop`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** SMS drops a day: each enabled time sends one drop per list group on it. */
export function dropCount(draft: Pick<ContainerDraft, 'slots'>): number {
  return draft.slots.filter((s) => s.enabled).reduce((n, s) => n + Math.max(0, s.listGroupIds.length), 0);
}

/** The Drop slots card's line: drop times, their pace (set there too since 20/10/2026), SMS drops (2+ groups on a time = 2+ drops). */
export function slotsSummary(draft: ContainerDraft): string {
  if (draft.slots.length === 0) return 'none yet';
  const on = draft.slots.filter((s) => s.enabled);
  const drops = dropCount(draft);
  const off = draft.slots.length - on.length;
  return [plural(on.length, 'time'), `every ${draft.intervalMin} min`, plural(drops, 'drop'), off > 0 ? `${off} off` : ''].filter(Boolean).join(' · ');
}

/** The day strip's span label — only drops that really plan count. */
export function spanLabel(draft: ContainerDraft): string {
  const active = draft.slots.filter((s) => s.enabled && s.listGroupIds.length > 0).length;
  if (active === 0) return 'add drops to see the day span';
  return `${plural(active, 'drop time')} · every ${draft.intervalMin} min`;
}
